import { type ReactNode } from "react";
import { ArrowLeftIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { TooltipInfoIcon } from "@vega-core/components/Tooltip/TooltipInfoIcon";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { MobileBreadcrumb } from "../Navigation/MobileBreadcrumbs/MobileBreadcrumb";

type BackButtonProps = {
  text?: string;
  onClick: VoidFunction;
};

function BackButton({ text, onClick }: BackButtonProps) {
  return (
    <div className="flex items-center text-text/secondary">
      <ThemedButton
        className="underline"
        dataTestId="back-button"
        icon={<ArrowLeftIcon />}
        onClick={onClick}
        variant="tertiary"
      >
        {text}
      </ThemedButton>
    </div>
  );
}

type Props = {
  action?: ReactNode;
  className?: string;
  goBack?: VoidFunction;
  isLoading?: boolean;
  size?: "large" | "regular" | "small";
  sticky?: boolean;
  title: ReactNode | string;
  description?: ReactNode | string;
  backButtonText?: string;
  backButtonAbove?: boolean;
  capitalize?: boolean;
  icon?: ReactNode;
  showDrawer?: boolean;
  fundName?: ReactNode | string;
  tooltip?: string;
  tooltipTitle?: string;
};

export function PageHeader({
  action,
  className,
  goBack,
  isLoading,
  sticky,
  title,
  size = "regular",
  description,
  backButtonText,
  backButtonAbove,
  capitalize = true,
  icon,
  showDrawer = false,
  fundName,
  tooltip,
  tooltipTitle,
}: Props) {
  const { theme } = useTheme();
  const isMobile = !useDeviceWidth("md");
  const hasInlineBackButton =
    !backButtonAbove && isTruthy(goBack) && !showDrawer;

  const getSizeClasses = () => {
    switch (size) {
      case "large":
        return "heading-3";
      case "small":
        return "heading-5";
      default:
        return "heading-4";
    }
  };

  return (
    <div
      className={cnTwMerge(
        "w-full",
        theme,
        {
          "sticky top-0 z-10 bg-background/primary": sticky,
        },
        className,
      )}
    >
      <div
        className={cnTwMerge("flex flex-col justify-between", {
          "items-center": !backButtonAbove,
          "items-start": backButtonAbove,
          "md:flex-row md:items-center": !backButtonAbove,
          "py-4 md:py-8": !goBack,
          "py-4 md:pt-8 md:pb-0": goBack,
        })}
      >
        {backButtonAbove && isTruthy(goBack) && !showDrawer ? (
          <div className="mb-4">
            <BackButton onClick={goBack} text={backButtonText} />
          </div>
        ) : null}

        <div className="flex items-center gap-4 w-full flex-wrap">
          {hasInlineBackButton ? (
            <BackButton onClick={goBack} text={backButtonText} />
          ) : null}

          {icon ? (
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
              {icon}
            </div>
          ) : null}

          <div className="flex flex-col flex-1">
            <div className="flex items-center gap-4">
              {showDrawer && isMobile ? (
                <MobileBreadcrumb
                  backButtonText={backButtonText}
                  onNavigateBack={goBack}
                />
              ) : null}
              <h1 className="flex flex-col">
                {fundName && !hasInlineBackButton ? (
                  <div
                    className="body-base-regular text-text/secondary"
                    data-ph-mask=""
                  >
                    {fundName}
                  </div>
                ) : null}
                <div
                  className={cnTwMerge(
                    "font-semibold text-text/primary md:mb-0 md:font-bold",
                    getSizeClasses(),
                    {
                      "skeleton-box w-60 h-8": isLoading,
                    },
                  )}
                >
                  <div
                    className={cnTwMerge("flex items-center gap-2", {
                      capitalize,
                    })}
                    data-testid="page-header-title"
                  >
                    {title}
                    {isTruthy(tooltip) && (
                      <TooltipInfoIcon content={tooltip} title={tooltipTitle} />
                    )}
                  </div>
                </div>
              </h1>
            </div>
            {description ? (
              <div
                className="body-base-regular text-text/secondary mt-1"
                data-testid="page-header-description"
              >
                {description}
              </div>
            ) : null}
          </div>

          {isTruthy(action) && (
            <div className="flex items-center gap-2">{action}</div>
          )}
        </div>
      </div>
    </div>
  );
}
